/**
 * Connected accounts — Settings section (APPLE_HIDDEN_EMAIL_LINKING.md §6, card 4).
 *
 * The destination the login-page notices point at. A user whose Apple sign-in
 * bounced because "Hide My Email" hid their address signs in the way they
 * normally do, comes here, and attaches Apple to the account they already have
 * — which is what makes §3 scenario 4 recoverable instead of a duplicate
 * account.
 *
 * Two link paths, chosen by platform:
 *
 *  - **Web** — ask the backend for a provider authorize URL and navigate to it.
 *    The link happens at Supabase; the provider returns the browser here.
 *  - **Native** — take the `id_token` from the OS sheet (the same
 *    `getAppleIdToken()`/`getGoogleIdToken()` the login page uses) and post it.
 *    A bundled Capacitor build cannot use the redirect at all: its webview
 *    origin is `https://localhost`, which is not a registrable Supabase
 *    redirect target. So on native the id_token path is not an optimisation,
 *    it is the only one that works — which is why native Android hides the
 *    Apple button entirely, exactly as LoginPage does.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Link2, Loader2, ShieldCheck, Unlink } from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';
import { isIOS, isNative } from '@/utils/platform';
import { getAppleIdToken } from '@/services/nativeAppleAuth';
import { getGoogleIdToken } from '@/services/nativeGoogleAuth';
import {
  connectedAccountsQueryKey,
  describeProviderRedirectError,
  linkProviderWithIdToken,
  providerLabel,
  startProviderLink,
  useConnectedAccounts,
  useUnlinkIdentity,
  IDENTITY_MOVE_STEPS,
  type ConnectedAccounts,
  type IdentityConflict,
  type LinkableProvider,
} from '@/api/extensions/useConnectedAccounts';

/**
 * Where the provider redirect comes back to. Carries the provider so the
 * success toast can name it, and is the same path Cards 1–3 tell the user to
 * visit (`/settings`).
 */
const RETURN_PATH = '/settings';

/**
 * Apple linking is offered wherever Apple sign-in is: everywhere on the web,
 * and on native iOS. Native Android has no working Apple path — see the module
 * note and `APPLE_SIGN_IN_AVAILABLE` in LoginPage.
 */
const APPLE_LINK_AVAILABLE = !isNative || isIOS;

const LINKABLE: Array<{ provider: LinkableProvider; available: boolean; blurb: string }> = [
  {
    provider: 'apple',
    available: APPLE_LINK_AVAILABLE,
    blurb: 'Sign in with Apple, including when Apple hides your email address.',
  },
  { provider: 'google', available: true, blurb: 'Sign in with your Google account.' },
];

/**
 * Read the outcome of a provider redirect off the URL.
 *
 * Both the query string and the hash are checked: which one GoTrue uses for an
 * error depends on the flow it resolved the request as, and a link that
 * silently did nothing is worse than a wrong-looking parse.
 */
function readLinkReturn(search: string, hash: string) {
  const query = new URLSearchParams(search);
  const fragment = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  const pick = (key: string) => query.get(key) || fragment.get(key);

  const errorCode = pick('error_code');
  const error = pick('error');
  const provider = query.get('link');

  return {
    provider,
    errorCode,
    errorDescription: pick('error_description'),
    hasError: !!(errorCode || error),
    // `code` is the unusable PKCE authorization code (see the service note);
    // it is stripped along with everything else rather than left on screen.
    hasAnything: !!(provider || errorCode || error || pick('code')),
  };
}

export default function ConnectedAccountsCard() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError, showInfo } = useToast();

  const { data, isLoading, isError, refetch } = useConnectedAccounts();
  const unlink = useUnlinkIdentity();

  const [linkingProvider, setLinkingProvider] = useState<LinkableProvider | null>(null);
  /** Identity id awaiting a second click before it is actually disconnected. */
  const [confirmingUnlink, setConfirmingUnlink] = useState<string | null>(null);
  /**
   * A provider already attached to a DIFFERENT account. Held in state and
   * rendered inline rather than raised as a toast: this is a four-step recovery
   * the user has to read and act on, and a toast that auto-dismisses is the one
   * shape guaranteed to lose it.
   */
  const [conflict, setConflict] = useState<IdentityConflict | null>(null);

  // The redirect return is handled exactly once. Without the guard a re-render
  // between reading the params and the navigate() that strips them replays the
  // toast.
  const handledReturn = useRef(false);

  useEffect(() => {
    if (handledReturn.current) return;

    const outcome = readLinkReturn(location.search, location.hash);
    if (!outcome.hasAnything) return;
    handledReturn.current = true;

    if (outcome.hasError) {
      const message = describeProviderRedirectError(outcome.errorCode, outcome.errorDescription);
      if (outcome.errorCode === 'identity_already_exists') {
        // Recoverable — render the steps inline instead of a vanishing toast.
        setConflict({ message, steps: IDENTITY_MOVE_STEPS });
      } else {
        showError("Couldn't connect that account", message);
      }
    } else if (outcome.provider) {
      showSuccess(
        `${providerLabel(outcome.provider)} connected`,
        `You can now sign in with ${providerLabel(outcome.provider)}.`
      );
      void refetch();
    }

    // Strip the linking params (and the dead PKCE code) so a refresh does not
    // replay the toast and the URL stays clean.
    navigate(location.pathname, { replace: true });
  }, [location, navigate, refetch, showError, showSuccess]);

  const handleLink = async (provider: LinkableProvider) => {
    setLinkingProvider(provider);
    setConflict(null);
    try {
      if (isNative) {
        const credential =
          provider === 'apple' ? await getAppleIdToken() : await getGoogleIdToken();
        const result = await linkProviderWithIdToken({
          provider,
          idToken: credential.idToken,
          nonce: credential.nonce,
        });
        queryClient.setQueryData<ConnectedAccounts>(connectedAccountsQueryKey, {
          identities: result.identities,
          canUnlink: result.canUnlink,
        });
        if (result.alreadyLinked) {
          showInfo(
            'Already connected',
            `${providerLabel(provider)} is already connected to this account.`
          );
        } else {
          showSuccess(
            `${providerLabel(provider)} connected`,
            `You can now sign in with ${providerLabel(provider)}.`
          );
        }
        return;
      }

      const url = await startProviderLink(provider, `${RETURN_PATH}?link=${provider}`);
      if (!url) {
        showInfo(
          'Already connected',
          `${providerLabel(provider)} is already connected to this account.`
        );
        void refetch();
        return;
      }
      // Full-page navigation — nothing after this runs.
      window.location.assign(url);
    } catch (error: any) {
      // A dismissed OS sheet is not a failure and must not raise a toast.
      if (error?.code === 'USER_CANCELLED') return;
      if (error?.data?.code === 'IDENTITY_ALREADY_LINKED') {
        setConflict({
          message: error.data.error,
          // Prefer the server's steps; the local copy is the fallback for an
          // older API that predates them.
          steps: error.data.steps?.length ? error.data.steps : IDENTITY_MOVE_STEPS,
        });
        return;
      }
      showError(
        "Couldn't connect that account",
        error?.data?.error || error?.message || 'Please try again.'
      );
    } finally {
      setLinkingProvider(null);
    }
  };

  const handleUnlink = async (identityId: string, provider: string) => {
    if (confirmingUnlink !== identityId) {
      setConfirmingUnlink(identityId);
      return;
    }
    setConfirmingUnlink(null);
    try {
      await unlink.mutateAsync(identityId);
      showSuccess(
        `${providerLabel(provider)} disconnected`,
        `You can no longer sign in with ${providerLabel(provider)}.`
      );
    } catch (error: any) {
      showError(
        "Couldn't disconnect that account",
        error?.data?.error || error?.message || 'Please try again.'
      );
    }
  };

  const identities = data?.identities ?? [];
  const canUnlink = data?.canUnlink ?? false;
  const linkedProviders = new Set(identities.map((identity) => identity.provider));

  return (
    <Card id="connected-accounts">
      <CardHeader className="bg-gradient-to-r from-indigo-50 to-blue-50 border-b">
        <CardTitle className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-indigo-100 to-blue-200 rounded-xl flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-indigo-600" />
          </div>
          <span>Connected Accounts</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        <p className="text-sm text-gray-600">
          Connect Apple or Google to sign in with them as well as your email and password.
          Connecting Apple here also covers the case where Apple hides your email address.
        </p>

        {conflict && (
          <div
            role="alert"
            className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-3"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900 font-medium">{conflict.message}</p>
            </div>
            <ol className="list-decimal pl-9 space-y-1.5 text-sm text-amber-900">
              {conflict.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
            <Button
              size="sm"
              variant="outline"
              className="ml-9"
              onClick={() => setConflict(null)}
            >
              Dismiss
            </Button>
          </div>
        )}

        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading your connected accounts…
          </div>
        )}

        {isError && (
          <p className="text-sm text-red-600">
            We couldn't load your connected accounts. Please refresh and try again.
          </p>
        )}

        {!isLoading && !isError && (
          <div className="space-y-2">
            {identities.map((identity) => (
              <div
                key={identity.identityId}
                className="flex items-center justify-between gap-4 p-4 rounded-xl border border-gray-200"
              >
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900">
                    {providerLabel(identity.provider)}
                  </div>
                  <div className="text-sm text-gray-600 truncate">
                    {identity.isPrivateEmail
                      ? `${identity.email ?? 'Hidden address'} (Apple is hiding your email)`
                      : identity.email ?? 'Connected'}
                  </div>
                </div>
                {canUnlink ? (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={unlink.isPending}
                    onClick={() => handleUnlink(identity.identityId, identity.provider)}
                    className={
                      confirmingUnlink === identity.identityId
                        ? 'border-red-400 text-red-700 hover:bg-red-50'
                        : 'text-gray-700'
                    }
                  >
                    <Unlink className="w-4 h-4 mr-2" />
                    {confirmingUnlink === identity.identityId ? 'Confirm' : 'Disconnect'}
                  </Button>
                ) : (
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    Only sign-in method
                  </span>
                )}
              </div>
            ))}

            {LINKABLE.filter(
              (option) => option.available && !linkedProviders.has(option.provider)
            ).map((option) => (
              <div
                key={option.provider}
                className="flex items-center justify-between gap-4 p-4 rounded-xl border border-dashed border-gray-300"
              >
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900">
                    {providerLabel(option.provider)}
                  </div>
                  <div className="text-sm text-gray-600">{option.blurb}</div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={linkingProvider !== null}
                  onClick={() => handleLink(option.provider)}
                >
                  {linkingProvider === option.provider ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Link2 className="w-4 h-4 mr-2" />
                  )}
                  Connect
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
